/**
 * QuoteAdminImport — /admin/quotes/import
 * Admin-only bulk import. Paste multiple lines in the format:
 *     "quote text" — Author Name
 * Backend auto-dedupes and reports created/duplicate/skipped counts.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, ArrowLeft, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "philosophy","courage","success","science","literature","leadership",
  "kindness","character","wit","time","purpose","inspiration",
  "family","community","business","education","health","travel",
  "memorial","heritage","india","faith","environment",
];

export default function QuoteAdminImport() {
  const { user, token } = useAuth();
  const [text, setText] = useState('');
  const [category, setCategory] = useState('inspiration');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [seeding, setSeeding] = useState(false);

  if (!user?.is_admin && user?.role !== "admin") {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center">
        <h1 className="font-display text-3xl mb-2">Admin only</h1>
        <Link to="/" className="wofy-link">← Home</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setLoading(true); setResult(null);
    try {
      const r = await axios.post(
        `${API}/api/quotes/bulk-import`,
        { text, category },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(r.data);
      toast.success(`Imported ${r.data.created} new · ${r.data.duplicates} duplicates · ${r.data.skipped} skipped`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setLoading(false); }
  };

  const seedDefault = async () => {
    setSeeding(true);
    try {
      const r = await axios.post(
        `${API}/api/quotes/seed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Seeded ${r.data.created} · ${r.data.duplicates} existing`);
      setResult({ created: r.data.created, duplicates: r.data.duplicates, skipped: 0, errors: r.data.errors, details: {} });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Seed failed");
    } finally { setSeeding(false); }
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4" data-testid="quote-admin-import">
      <Link to="/" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="font-display text-3xl">Quote Bulk Import</h1>
        <button onClick={seedDefault} disabled={seeding}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm hover:shadow-md"
          style={{ borderColor: "var(--wofy-border)" }} data-testid="seed-btn">
          {seeding ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Seeding…</> : <><Sparkles className="w-3.5 h-3.5" />Seed built-in 200 quotes</>}
        </button>
      </div>
      <p className="text-sm opacity-70 mb-4">Paste quotes, one per line. Format: <code className="px-1 rounded" style={{ background: "var(--wofy-surface)" }}>“Quote text” — Author Name</code></p>

      <form onSubmit={submit} className="wofy-card !p-5 space-y-3">
        <div>
          <label className="block text-sm font-medium mb-1">Default category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="category-select">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <textarea
          rows={12}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={`"Be the change that you wish to see in the world." — Mahatma Gandhi\n"Injustice anywhere is a threat to justice everywhere." — Martin Luther King Jr.`}
          className="w-full px-3 py-2 rounded-lg border font-mono text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="import-textarea"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs opacity-60">Duplicates (same author + first 60 chars) are skipped automatically.</span>
          <button type="submit" disabled={loading || !text.trim()} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="import-submit">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Importing…</> : <><Upload className="w-4 h-4" />Import</>}
          </button>
        </div>
      </form>

      {result && (
        <section className="mt-6 wofy-card !p-5" data-testid="import-result">
          <h2 className="font-display text-xl mb-3">Result</h2>
          <div className="grid grid-cols-4 gap-3 mb-4 text-center">
            <div className="p-3 rounded-lg" style={{ background: "#DCFCE7" }}>
              <div className="text-2xl font-display" data-testid="result-created">{result.created}</div>
              <div className="text-xs opacity-70">Created</div>
            </div>
            <div className="p-3 rounded-lg" style={{ background: "#FEF3C7" }}>
              <div className="text-2xl font-display">{result.duplicates}</div>
              <div className="text-xs opacity-70">Duplicates</div>
            </div>
            <div className="p-3 rounded-lg" style={{ background: "#F1F5F9" }}>
              <div className="text-2xl font-display">{result.skipped}</div>
              <div className="text-xs opacity-70">Skipped</div>
            </div>
            <div className="p-3 rounded-lg" style={{ background: "#FEE2E2" }}>
              <div className="text-2xl font-display">{result.errors}</div>
              <div className="text-xs opacity-70">Errors</div>
            </div>
          </div>

          {result.details?.created?.length > 0 && (
            <details open>
              <summary className="cursor-pointer text-sm font-medium mb-2 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-green-600" />Created ({result.details.created.length})</summary>
              <ul className="text-xs space-y-1 max-h-48 overflow-y-auto pl-4">
                {result.details.created.map((c, i) => (
                  <li key={i}>“{c.text}” <span className="opacity-60">— {c.author}</span></li>
                ))}
              </ul>
            </details>
          )}
          {result.details?.duplicates?.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-sm font-medium mb-2 inline-flex items-center gap-1"><AlertCircle className="w-4 h-4 text-amber-600" />Duplicates ({result.details.duplicates.length})</summary>
              <ul className="text-xs space-y-1 max-h-48 overflow-y-auto pl-4">
                {result.details.duplicates.map((c, i) => <li key={i}>“{c.text}” <span className="opacity-60">— {c.author}</span></li>)}
              </ul>
            </details>
          )}
          {result.details?.skipped?.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-sm font-medium mb-2">Skipped ({result.details.skipped.length})</summary>
              <ul className="text-xs space-y-1 max-h-48 overflow-y-auto pl-4">
                {result.details.skipped.map((c, i) => <li key={i}><code>{c.line}</code> — {c.reason}</li>)}
              </ul>
            </details>
          )}
        </section>
      )}
    </div>
  );
}
